/**
 * Runs at scale for the distribution mock: one distribution with a long run history (paging),
 * a partly failed run with per-delivery errors, test runs, a live run, and distributions that are
 * paused, finished, archived and draft. Neutral generated names; ids all start with `dist-scale-`.
 * Append-only: never touches an existing record. The integrator calls
 * `seedDistributionRunsAtScale(store)` once at the end of seedStory.
 */
import { MOCK_FIELDS } from "./distributions.mockData";
import type { DistributionApi, RecipientInputApi } from "./distributions.types";
import { buildMockRun, countsOf, type MockRunRecord } from "./distributionsMockRuns";
import type { RecipientRuleContext } from "./distributionRecipientRules";

/** The slice of the mock store this file needs; the real store satisfies it structurally. */
export interface ScaleSeedRecord {
  dist: DistributionApi;
  rows: RecipientInputApi[];
  runs: MockRunRecord[];
  runsDone: number;
}
export interface ScaleSeedStore {
  distributions: Map<string, ScaleSeedRecord>;
}

export const SCALE_DISTRIBUTION_IDS = {
  busy: "dist-scale-busy",
  paused: "dist-scale-paused",
  finished: "dist-scale-finished",
  archived: "dist-scale-archived",
  draft: "dist-scale-draft",
} as const;

/** How many runs the busy distribution holds (45 or more so paging works at the default 20). */
export const SCALE_BUSY_RUN_COUNT = 52;
export const SCALE_PARTLY_FAILED_RUN_ID = "run-scale-partly-failed";
export const SCALE_LIVE_RUN_ID = "run-scale-live";

const RECIPIENT_COUNT = 8;
const DAY_MS = 86_400_000;
/** The newest seeded history starts here, so seeds never depend on the clock. */
const NEWEST_RUN_MS = Date.parse("2026-10-20T15:00:00Z");

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const pad = (n: number): string => String(n).padStart(2, "0");

const failureTexts = [
  "Bounced: mailbox full. Free up space, or change the address in Recipients, then retry.",
  "Bounced: the address doesn’t accept mail from this sender. Check it in Recipients, then retry.",
  "The mail server timed out. Retry the delivery.",
];

const rowsFor = (values: readonly string[], prefix: string): RecipientInputApi[] =>
  Array.from({ length: RECIPIENT_COUNT }, (_, i) => ({
    position: i + 1,
    value: values[i % values.length],
    name: `Recipient ${pad(i + 1)}`,
    channel: "email",
    address: `${prefix}-recipient-${pad(i + 1)}@example.com`,
    cc: [],
    formats: null,
    template_id: null,
    theme_id: null,
    brand: null,
    extra: {},
    source: "manual",
  }));

const ctxFor = (dist: DistributionApi, rows: RecipientInputApi[], values: readonly string[]): RecipientRuleContext => ({
  fieldLabel: dist.split?.label ?? "value",
  values: [...values],
  excludedValues: [],
  templateIds: [dist.content.template_id],
  themeIds: [],
  rows,
  slackChannelsWithoutApp: new Set<string>(),
});

type RunKind = "schedule" | "now" | "test" | "preview";

/** The trigger of the n-th run, newest first: mostly scheduled, with a few of each other kind. */
const kindOf = (n: number): RunKind => (n % 11 === 3 ? "test" : n % 13 === 5 ? "now" : n % 17 === 7 ? "preview" : "schedule");

const makeRun = (
  dist: DistributionApi,
  rows: RecipientInputApi[],
  values: readonly string[],
  id: string,
  trigger: RunKind,
  startedAtMs: number,
  failures?: Record<number, string>,
): MockRunRecord => {
  const positions = trigger === "test" ? [1] : trigger === "preview" ? [1, 2] : undefined;
  return buildMockRun({
    runId: id,
    trigger,
    dist,
    rows: positions ? rows.filter((r) => positions.includes(r.position)) : rows,
    ctx: ctxFor(dist, rows, values),
    includedValues: values,
    emptyValues: [],
    startedAtMs,
    testAddress: "you@northwind.com",
    failures,
  });
};

/** A run caught in the middle: some copies sent, the rest still sending. Stays as is when read. */
const liveRun = (run: MockRunRecord): MockRunRecord => {
  const { detail } = run;
  const half = Math.ceil(detail.deliveries.length / 2);
  detail.deliveries = detail.deliveries.map((d, i) => (i < half ? d : { ...d, status: "sending", error: null, sent_at: null }));
  detail.slices = detail.slices.map((s, i) => (i < Math.ceil(detail.slices.length / 2) ? s : { ...s, status: "rendering", files: [] }));
  Object.assign(detail.run, {
    status: "running",
    finished_at: null,
    error: null,
    counts: countsOf(detail.deliveries.slice(0, half), Math.ceil(detail.slices.length / 2)),
  });
  return run;
};

const stamp = (dist: DistributionApi, status: DistributionApi["status"], name: string, id: string, runs: number): DistributionApi => ({
  ...clone(dist),
  id,
  name,
  status,
  runs_count: runs,
  run_count: runs,
  next_run_at: null,
  last_run: null,
  activated_at: status === "draft" ? null : "2026-06-01T09:00:00Z",
  created_at: "2026-05-28T09:00:00Z",
  updated_at: "2026-10-20T15:05:00Z",
});

/**
 * Appends five distributions to the first story store: a busy one with SCALE_BUSY_RUN_COUNT runs
 * (including a partly failed one, tests and a live one), plus paused, finished, archived and draft.
 * Idempotent: does nothing when already seeded, or when the store has no distribution to copy from.
 */
export const seedDistributionRunsAtScale = (store: ScaleSeedStore): void => {
  if (store.distributions.has(SCALE_DISTRIBUTION_IDS.busy)) return;
  const template = Array.from(store.distributions.values()).find((r) => r.dist.split)?.dist;
  if (!template) return;
  const field = MOCK_FIELDS.find((f) => f.label === template.split?.label) ?? MOCK_FIELDS[0];
  const values = field.values.slice(0, 5);
  const add = (dist: DistributionApi, rows: RecipientInputApi[], runs: MockRunRecord[], runsDone: number) =>
    store.distributions.set(dist.id, { dist: { ...dist, recipient_count: rows.length }, rows, runs, runsDone });
  const build = (id: string, status: DistributionApi["status"], name: string, kinds: RunKind[], endedMs: number, withFailure = false) => {
    const dist = stamp(template, status, name, id, 0);
    const rows = status === "draft" ? [] : rowsFor(values, id);
    const runs = kinds.map((kind, n) =>
      makeRun(dist, rows, values, `run-${id.replace("dist-", "")}-${pad(n + 1)}`, kind, endedMs - n * 7 * DAY_MS, withFailure && n === 0 ? { 2: failureTexts[0] } : undefined),
    );
    const sends = runs.filter((r) => r.detail.run.trigger === "now" || r.detail.run.trigger === "schedule").length;
    return { dist: { ...dist, runs_count: sends, run_count: sends }, rows, runs, sends };
  };

  // Busy: newest first. Position 0 is live, 1 partly failed, then a long mixed history.
  const busyDist = stamp(template, "active", "Daily performance digest", SCALE_DISTRIBUTION_IDS.busy, 0);
  const busyRows = rowsFor(values, "busy");
  const busyRuns: MockRunRecord[] = [];
  for (let n = 0; n < SCALE_BUSY_RUN_COUNT; n += 1) {
    const startedAt = NEWEST_RUN_MS - n * DAY_MS;
    if (n === 0) busyRuns.push(liveRun(makeRun(busyDist, busyRows, values, SCALE_LIVE_RUN_ID, "now", startedAt)));
    else if (n === 1) {
      busyRuns.push(
        makeRun(busyDist, busyRows, values, SCALE_PARTLY_FAILED_RUN_ID, "schedule", startedAt, {
          2: failureTexts[0],
          5: failureTexts[1],
          7: failureTexts[2],
        }),
      );
    } else busyRuns.push(makeRun(busyDist, busyRows, values, `run-scale-busy-${pad(n)}`, kindOf(n), startedAt));
  }
  const busySends = busyRuns.filter((r) => r.detail.run.trigger === "now" || r.detail.run.trigger === "schedule").length;
  add(
    {
      ...busyDist,
      runs_count: busySends,
      run_count: busySends,
    },
    busyRows,
    busyRuns,
    busySends,
  );

  const paused = build(SCALE_DISTRIBUTION_IDS.paused, "paused", "Weekly digest (paused)", ["schedule", "schedule", "test"], NEWEST_RUN_MS - 21 * DAY_MS);
  add(paused.dist, paused.rows, paused.runs, paused.sends);
  const finished = build(SCALE_DISTRIBUTION_IDS.finished, "completed", "Quarter-end pack (finished)", ["schedule", "schedule", "schedule", "now"], NEWEST_RUN_MS - 40 * DAY_MS);
  add(finished.dist, finished.rows, finished.runs, finished.sends);
  const archived = build(SCALE_DISTRIBUTION_IDS.archived, "archived", "Pilot digest (archived)", ["schedule", "test"], NEWEST_RUN_MS - 90 * DAY_MS, true);
  add(archived.dist, archived.rows, archived.runs, archived.sends);
  const draft = build(SCALE_DISTRIBUTION_IDS.draft, "draft", "New digest (draft)", [], NEWEST_RUN_MS);
  add(draft.dist, draft.rows, draft.runs, 0);
};
